import { useState, useEffect, useCallback } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import {
  monitorList, monitorRun, monitorAck, monitorHistory,
  monitorAdd, monitorDelete, monitorGetConfig, monitorSetConfig,
} from '../../lib/adminApi';

const NAVY   = '#0a2540';
const ORANGE = '#F37428';

function timeAgo(d) {
  if (!d) return '—';
  const m = Math.floor((Date.now() - new Date(d)) / 60000);
  if (m < 2)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function fmtTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function StatusDot({ url }) {
  const ok      = url.last_status >= 200 && url.last_status < 300;
  const changed = ok && url.last_changed_at && !url.change_ack;
  const error   = url.last_status === 0 || url.last_status >= 400;
  const unseen  = !url.last_checked_at;

  if (unseen)  return <span style={dot('#94a3b8')} title="Never checked">○</span>;
  if (error)   return <span style={dot('#ef4444')} title={`HTTP ${url.last_status || 'error'}`}>✕</span>;
  if (changed) return <span style={dot(ORANGE)}    title="Page changed — needs review">!</span>;
  return             <span style={dot('#10b981')}  title="OK">✓</span>;
}

function dot(color) {
  return {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 22, height: 22, borderRadius: '50%', background: color + '22',
    color, fontWeight: 700, fontSize: 13, border: `1.5px solid ${color}`,
  };
}

function CategoryBadge({ cat }) {
  const MAP = {
    marketing: ['#ede9fe', '#7c3aed', 'Marketing'],
    app:       ['#dbeafe', '#1d4ed8', 'App'],
    api:       ['#f0fdf4', '#15803d', 'API'],
    custom:    ['#f1f5f9', '#64748b', 'Custom'],
  };
  const [bg, color, label] = MAP[cat] || MAP.custom;
  return (
    <span style={{ background: bg, color, fontSize: 10, fontWeight: 700,
      padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

export default function PageMonitor() {
  const [urls, setUrls]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [running, setRunning]   = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [history, setHistory]   = useState({});
  const [histLoading, setHistLoading] = useState(null);
  const [addOpen, setAddOpen]   = useState(false);
  const [newUrl, setNewUrl]     = useState({ label: '', url: '', category: 'marketing' });
  const [adding, setAdding]     = useState(false);
  const [runResult, setRunResult] = useState(null);
  // Notification settings
  const [notifyEmails, setNotifyEmails] = useState([]);
  const [newEmail, setNewEmail]         = useState('');
  const [savingEmails, setSavingEmails] = useState(false);

  const load = useCallback(async () => {
    try {
      const [data, cfg] = await Promise.all([monitorList(), monitorGetConfig()]);
      setUrls(data.urls || []);
      setNotifyEmails(cfg?.config?.notify_emails || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function runNow() {
    setRunning(true);
    setRunResult(null);
    try {
      const res = await monitorRun();
      setRunResult(res);
      await load();
    } catch (e) { alert(e.message); }
    finally { setRunning(false); }
  }

  async function acknowledge(id) {
    try {
      await monitorAck(id);
      setUrls(u => u.map(x => x.monitor_id === id ? { ...x, change_ack: true } : x));
    } catch (e) { alert(e.message); }
  }

  async function remove(id, label) {
    if (!confirm(`Remove "${label}" from monitoring?`)) return;
    try {
      await monitorDelete(id);
      setUrls(u => u.filter(x => x.monitor_id !== id));
    } catch (e) { alert(e.message); }
  }

  async function toggleHistory(id) {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    if (!history[id]) {
      setHistLoading(id);
      try {
        const data = await monitorHistory(id);
        setHistory(h => ({ ...h, [id]: data.history || [] }));
      } catch (_) {}
      finally { setHistLoading(null); }
    }
  }

  async function addUrl() {
    if (!newUrl.label || !newUrl.url) return;
    setAdding(true);
    try {
      await monitorAdd(newUrl);
      setAddOpen(false);
      setNewUrl({ label: '', url: '', category: 'marketing' });
      await load();
    } catch (e) { alert(e.message); }
    finally { setAdding(false); }
  }

  async function addEmail() {
    const email = newEmail.trim().toLowerCase();
    if (!email || notifyEmails.includes(email)) { setNewEmail(''); return; }
    const updated = [...notifyEmails, email];
    setSavingEmails(true);
    try {
      await monitorSetConfig('notify_emails', updated);
      setNotifyEmails(updated);
      setNewEmail('');
    } catch (e) { alert(e.message); }
    finally { setSavingEmails(false); }
  }

  async function removeEmail(email) {
    const updated = notifyEmails.filter(e => e !== email);
    setSavingEmails(true);
    try {
      await monitorSetConfig('notify_emails', updated);
      setNotifyEmails(updated);
    } catch (e) { alert(e.message); }
    finally { setSavingEmails(false); }
  }

  const needsAttention = urls.filter(u =>
    (u.last_status === 0 || u.last_status >= 400) ||
    (u.last_changed_at && !u.change_ack)
  ).length;

  if (loading) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: '#f0f5fb' }}>
        <AdminSidebar active="monitor" />
        <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ color: '#94a3b8' }}>Loading…</div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f0f5fb',
      fontFamily: "'Inter', system-ui, sans-serif" }}>
      <AdminSidebar active="monitor" />
      <main style={{ flex: 1, overflowY: 'auto' }}>

        {/* Hero Banner */}
        <div style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #0f3460 50%, #162d4a 100%)`,
          padding: '32px 36px 28px', marginBottom: 0,
          position: 'relative', overflow: 'hidden',
        }}>
          {/* subtle grid pattern */}
          <div style={{ position: 'absolute', inset: 0, opacity: 0.04,
            backgroundImage: 'repeating-linear-gradient(0deg,#fff 0px,#fff 1px,transparent 1px,transparent 40px),repeating-linear-gradient(90deg,#fff 0px,#fff 1px,transparent 1px,transparent 40px)' }} />

          <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18 }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: 'rgba(243,116,40,0.15)',
                border: '1px solid rgba(243,116,40,0.3)', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 26, flexShrink: 0 }}>
                🔭
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#fff' }}>Scout</h1>
                  <span style={{ background: 'rgba(243,116,40,0.2)', color: ORANGE, fontSize: 9,
                    fontWeight: 700, letterSpacing: '0.1em', padding: '3px 8px', borderRadius: 10,
                    textTransform: 'uppercase', border: '1px solid rgba(243,116,40,0.3)' }}>Bot 1</span>
                </div>
                <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: 13, maxWidth: 520, lineHeight: 1.5 }}>
                  Checks every page on your marketing site and app every 2 hours. Detects changes, monitors uptime, and alerts you by email when something shifts.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
              <button onClick={() => setAddOpen(true)} style={s.btnHeroSecondary}>+ Add page</button>
              <button onClick={runNow} disabled={running} style={s.btnHeroPrimary}>
                {running ? '⟳ Running…' : '▶ Run now'}
              </button>
            </div>
          </div>

          {/* Stat pills */}
          <div style={{ position: 'relative', display: 'flex', gap: 10, marginTop: 24 }}>
            {[
              { label: 'Monitored',       val: urls.length,                                                                                                  color: 'rgba(255,255,255,0.7)' },
              { label: 'All clear',        val: urls.filter(u => u.last_checked_at && u.last_status >= 200 && u.last_status < 300 && (!u.last_changed_at || u.change_ack)).length, color: '#34d399' },
              { label: 'Changes',          val: urls.filter(u => u.last_changed_at && !u.change_ack).length,                                                  color: ORANGE },
              { label: 'Errors',           val: urls.filter(u => u.last_checked_at && (u.last_status === 0 || u.last_status >= 400)).length,                  color: '#f87171' },
              { label: 'Not yet checked',  val: urls.filter(u => !u.last_checked_at).length,                                                                  color: 'rgba(255,255,255,0.35)' },
            ].map(({ label, val, color }) => (
              <div key={label} style={{
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 10, padding: '8px 14px', minWidth: 80, textAlign: 'center',
              }}>
                <div style={{ fontSize: 20, fontWeight: 700, color, lineHeight: 1 }}>{val}</div>
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginTop: 3,
                  textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: '24px 36px' }}>

          {/* Run result banner */}
          {runResult && (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10,
              padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center',
              justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: '#15803d', fontWeight: 600 }}>
                ✓ Checked {runResult.checked} pages —&nbsp;
                {runResult.changed === 0 ? 'no changes detected' : `${runResult.changed} page(s) changed`}
              </span>
              <button onClick={() => setRunResult(null)}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 16 }}>×</button>
            </div>
          )}

          {/* Attention banner */}
          {needsAttention > 0 && (
            <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10,
              padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#c2410c', fontWeight: 600 }}>
              ⚠ {needsAttention} page{needsAttention > 1 ? 's' : ''} need attention — review below
            </div>
          )}

          {/* URL table */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden', marginBottom: 24 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  {['', 'Page', 'Category', 'HTTP', 'Last checked', 'Last changed', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700,
                      color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {urls.map(url => {
                  const isExpanded = expanded === url.monitor_id;
                  const rowChanged = url.last_changed_at && !url.change_ack;
                  const rowError   = url.last_checked_at && (url.last_status === 0 || url.last_status >= 400);
                  return (
                    <>
                      <tr key={url.monitor_id}
                        style={{ borderBottom: isExpanded ? 'none' : '1px solid #f1f5f9', cursor: 'pointer',
                          background: rowError ? '#fff5f5' : rowChanged ? '#fffbeb' : '#fff' }}
                        onClick={() => toggleHistory(url.monitor_id)}>
                        <td style={{ padding: '12px 14px' }}>
                          <StatusDot url={url} />
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ fontWeight: 600, color: NAVY }}>{url.label}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 340 }}>
                            {url.url}
                          </div>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <CategoryBadge cat={url.category} />
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          {url.last_status ? (
                            <span style={{ fontWeight: 600,
                              color: url.last_status < 300 ? '#10b981' : url.last_status < 400 ? ORANGE : '#ef4444' }}>
                              {url.last_status}
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ padding: '12px 14px', color: '#64748b', whiteSpace: 'nowrap' }}>
                          {timeAgo(url.last_checked_at)}
                        </td>
                        <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                          {url.last_changed_at ? (
                            <span style={{ color: url.change_ack ? '#94a3b8' : ORANGE, fontWeight: url.change_ack ? 400 : 600 }}>
                              {timeAgo(url.last_changed_at)}
                              {url.change_ack && <span style={{ fontSize: 10, marginLeft: 4, color: '#10b981' }}>✓ ack</span>}
                            </span>
                          ) : <span style={{ color: '#94a3b8' }}>—</span>}
                        </td>
                        <td style={{ padding: '12px 14px' }} onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            {rowChanged && (
                              <button onClick={() => acknowledge(url.monitor_id)} style={s.btnAck}>
                                ✓ Acknowledge
                              </button>
                            )}
                            <a href={url.url} target="_blank" rel="noreferrer"
                              style={{ fontSize: 11, color: '#94a3b8', textDecoration: 'none' }} title="Open page">↗</a>
                            <button onClick={() => remove(url.monitor_id, url.label)}
                              style={{ background: 'none', border: 'none', color: '#cbd5e1', cursor: 'pointer',
                                fontSize: 14, padding: '0 2px' }} title="Remove">✕</button>
                          </div>
                        </td>
                      </tr>

                      {/* History drawer */}
                      {isExpanded && (
                        <tr key={`${url.monitor_id}-h`}>
                          <td colSpan={7} style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', padding: 0 }}>
                            {histLoading === url.monitor_id ? (
                              <div style={{ padding: '16px 24px', color: '#94a3b8', fontSize: 12 }}>Loading history…</div>
                            ) : (history[url.monitor_id] || []).length === 0 ? (
                              <div style={{ padding: '16px 24px', color: '#94a3b8', fontSize: 12 }}>No history yet — run a check first</div>
                            ) : (
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                  <tr>
                                    {['Checked at', 'HTTP', 'Content hash', 'Size', 'Changed?', 'Error'].map(h => (
                                      <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10,
                                        fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase',
                                        letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {(history[url.monitor_id] || []).map((h, i) => (
                                    <tr key={h.log_id} style={{ borderBottom: '1px solid #f1f5f9',
                                      background: h.changed ? '#fffbeb' : i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                                      <td style={{ padding: '8px 14px', color: '#475569', whiteSpace: 'nowrap' }}>{fmtTime(h.checked_at)}</td>
                                      <td style={{ padding: '8px 14px', fontWeight: 600,
                                        color: h.http_status < 300 ? '#10b981' : h.http_status < 400 ? ORANGE : '#ef4444' }}>
                                        {h.http_status || '—'}
                                      </td>
                                      <td style={{ padding: '8px 14px', color: '#94a3b8', fontFamily: 'monospace', fontSize: 11 }}>
                                        {h.content_hash}
                                      </td>
                                      <td style={{ padding: '8px 14px', color: '#64748b' }}>
                                        {h.text_len ? `${(h.text_len / 1024).toFixed(1)} KB` : '—'}
                                      </td>
                                      <td style={{ padding: '8px 14px' }}>
                                        {h.changed
                                          ? <span style={{ color: ORANGE, fontWeight: 700 }}>⚡ Changed</span>
                                          : <span style={{ color: '#94a3b8' }}>—</span>}
                                      </td>
                                      <td style={{ padding: '8px 14px', color: '#ef4444', fontSize: 11 }}>
                                        {h.error_msg || '—'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>

            {urls.length === 0 && (
              <div style={{ padding: '60px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                No pages configured — click "+ Add page" to get started
              </div>
            )}
          </div>

          {/* Notification Settings */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '22px 24px' }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: NAVY, marginBottom: 4 }}>Alert Notifications</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                Scout sends an email to these addresses when a page changes or goes down.
              </div>
            </div>

            {/* Email list */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
              {notifyEmails.length === 0 && (
                <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>No notification emails configured</span>
              )}
              {notifyEmails.map(email => (
                <div key={email} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: '#f0f5fb', border: '1px solid #e2e8f0', borderRadius: 20,
                  padding: '4px 12px 4px 14px', fontSize: 12, color: NAVY,
                }}>
                  {email}
                  <button onClick={() => removeEmail(email)}
                    style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer',
                      padding: 0, fontSize: 13, lineHeight: 1, display: 'flex', alignItems: 'center' }}>
                    ×
                  </button>
                </div>
              ))}
            </div>

            {/* Add email input */}
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addEmail()}
                placeholder="Add email address…"
                type="email"
                style={{ flex: 1, maxWidth: 280, padding: '7px 10px', border: '1px solid #e2e8f0',
                  borderRadius: 7, fontSize: 13, outline: 'none' }}
              />
              <button onClick={addEmail} disabled={savingEmails || !newEmail.trim()} style={s.btnPrimary}>
                {savingEmails ? 'Saving…' : 'Add'}
              </button>
            </div>
          </div>

        </div>

        {/* Add URL modal */}
        {addOpen && (
          <div style={s.overlay} onClick={() => setAddOpen(false)}>
            <div style={s.modal} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '18px 22px', borderBottom: '1px solid #e2e8f0' }}>
                <span style={{ fontWeight: 700, fontSize: 15, color: NAVY }}>Add page to Scout</span>
                <button onClick={() => setAddOpen(false)} style={s.closeBtn}>✕</button>
              </div>
              <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={s.label}>Label</label>
                  <input value={newUrl.label} onChange={e => setNewUrl(n => ({ ...n, label: e.target.value }))}
                    placeholder="e.g. Pricing Page" style={s.input} />
                </div>
                <div>
                  <label style={s.label}>URL</label>
                  <input value={newUrl.url} onChange={e => setNewUrl(n => ({ ...n, url: e.target.value }))}
                    placeholder="https://..." style={s.input} />
                </div>
                <div>
                  <label style={s.label}>Category</label>
                  <select value={newUrl.category} onChange={e => setNewUrl(n => ({ ...n, category: e.target.value }))}
                    style={s.input}>
                    <option value="marketing">Marketing</option>
                    <option value="app">App</option>
                    <option value="api">API</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10,
                padding: '14px 22px', borderTop: '1px solid #e2e8f0' }}>
                <button onClick={() => setAddOpen(false)} style={s.btnSecondary}>Cancel</button>
                <button onClick={addUrl} disabled={adding || !newUrl.label || !newUrl.url} style={s.btnPrimary}>
                  {adding ? 'Adding…' : 'Add page'}
                </button>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}

const s = {
  btnPrimary:      { background: ORANGE, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnSecondary:    { background: '#fff', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer' },
  btnHeroPrimary:  { background: ORANGE, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnHeroSecondary:{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, padding: '9px 16px', fontSize: 13, cursor: 'pointer' },
  btnAck:          { background: '#f0fdf4', color: '#15803d', border: '1px solid #86efac', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  overlay:         { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal:           { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 480, overflow: 'hidden' },
  closeBtn:        { background: 'none', border: 'none', color: '#94a3b8', fontSize: 18, cursor: 'pointer' },
  label:           { display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 },
  input:           { width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' },
};
